import { aboutPageStats, aboutHighlightStat } from "../data/constants";
import AnimatedCounter from "../components/AnimatedCounter";
import HomeSectionHead from "../components/HomeSectionHead";
import SiteCta from "../components/SiteCta";

const baseUrl = process.env.PUBLIC_URL || "";

const pillars = [
  { icon: "bi-bullseye", title: "Tailored Strategy", desc: "Strategies aligned to your business goals and operational realities." },
  { icon: "bi-people-fill", title: "Expert Team", desc: "Certified professionals with experience at leading global technology companies." },
  { icon: "bi-arrow-up-circle", title: "Continuous Growth", desc: "We evolve with technology trends to keep your infrastructure future-ready." },
  { icon: "bi-gem", title: "Excellence First", desc: "Every engagement reflects our commitment to quality and precision." },
];

const storyHighlights = [
  "End-to-end IT infrastructure design & deployment",
  "Pan-India delivery with 24/7 support",
  "Trusted by enterprises, PSUs & growing businesses",
];

export default function AboutPage() {
  return (
    <div className="about-page">
      {/* Intro — compact centered header */}
      <section className="about-page-intro animate-on-scroll">
        <div className="container">
          <div className="about-page-intro-inner">
            <span className="about-page-intro-badge">Who We Are</span>
            <h1 className="about-page-intro-title">About Emicron Techsolutions</h1>
            <p className="about-page-intro-desc">
              A leading system integration company empowering businesses to thrive in the digital age.
            </p>
          </div>
          <div className="about-page-intro-stats">
            {aboutPageStats.map((s, i) => (
              <div key={s.label} className={`about-page-intro-stat${i < aboutPageStats.length - 1 ? " has-divider" : ""}`}>
                <AnimatedCounter value={s.value} suffix={s.suffix} className="about-page-intro-stat-num" as="div" />
                <span className="about-page-intro-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="about-page-section animate-on-scroll">
        <div className="container">
          <div className="home-panel-card">
            <div className="row g-4 g-lg-5 align-items-center">
              <div className="col-lg-6 order-lg-2">
                <div className="about-page-story-visual">
                  <img
                    src={`${baseUrl}/img/about-story.jpg`}
                    alt="Emicron team collaborating in a strategy meeting"
                    className="about-page-story-img"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="about-page-float-stat">
                    <AnimatedCounter value={aboutHighlightStat.value} suffix={aboutHighlightStat.suffix} className="about-page-float-stat-num" as="div" />
                    <div className="about-page-float-stat-label">{aboutHighlightStat.label}</div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 order-lg-1">
                <HomeSectionHead
                  label="Our Story"
                  title="Discover Your Tech Journey with Emicron"
                  desc="From Delhi to enterprises across India — we build IT foundations that scale."
                />
                <p className="about-page-text">
                  We specialize in designing, implementing, and managing robust IT infrastructure solutions that form the backbone of modern enterprise. Emicron helps organizations optimize their IT environments to enhance performance, ensure business continuity, and maximize operational efficiency.
                </p>
                <p className="about-page-text mb-0">
                  We are a technology partner you can trust — delivering tailored solutions aligned to your specific needs and business objectives.
                </p>
                <ul className="about-page-highlights">
                  {storyHighlights.map((item) => (
                    <li key={item}>
                      <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="about-page-section about-page-section--muted animate-on-scroll">
        <div className="container">
          <HomeSectionHead
            label="Our Purpose"
            title="Vision & Mission"
            desc="Guiding principles that shape every solution we design and deliver."
          />
          <div className="about-page-vm-grid">
            <article className="about-page-vm-card">
              <div className="about-showcase-vm-icon">
                <i className="bi bi-eye-fill"></i>
              </div>
              <h3 className="about-showcase-vm-title">Our Vision</h3>
              <p className="about-showcase-vm-text">
                To be India&apos;s most trusted system integration partner — enabling organizations to harness technology for sustainable growth, resilience, and innovation.
              </p>
            </article>
            <article className="about-page-vm-card">
              <div className="about-showcase-vm-icon about-showcase-vm-icon--orange">
                <i className="bi bi-rocket-takeoff-fill"></i>
              </div>
              <h3 className="about-showcase-vm-title">Our Mission</h3>
              <p className="about-showcase-vm-text">
                To empower businesses with robust and innovative system integration solutions. We understand your unique challenges, develop tailored strategies, and implement best-in-class technologies that foster efficiency and growth.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Core Pillars */}
      <section className="about-page-section animate-on-scroll">
        <div className="container">
          <HomeSectionHead
            label="Why Emicron"
            title="What Sets Us Apart"
            desc="The values and capabilities behind every engagement."
          />
          <div className="row g-4">
            {pillars.map((p) => (
              <div key={p.title} className="col-md-6 col-lg-3">
                <article className="about-page-pillar-card">
                  <div className="about-page-pillar-icon">
                    <i className={`bi ${p.icon}`}></i>
                  </div>
                  <h4>{p.title}</h4>
                  <p>{p.desc}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Director */}
      <section className="about-page-section about-page-section--center animate-on-scroll">
        <div className="container">
          <HomeSectionHead label="Leadership" title="Director's Desk" />
          <div className="about-page-director-wrap">
            <div className="director-card about-page-director-quote">
              <p className="director-quote">
                &ldquo;We believe technology has the power to transform businesses. At Emicron, we&apos;re dedicated to helping our clients harness that power to achieve their goals. Our focus on innovation, unwavering commitment to excellence, and collaborative teamwork enable us to deliver impactful solutions that make a real difference.&rdquo;
              </p>
              <div className="director-name">Rahul Mathur</div>
              <div className="director-role">Chairman, Emicron Techsolutions Pvt Ltd</div>
            </div>
          </div>
        </div>
      </section>

      <SiteCta
        title="Ready to Transform Your IT Infrastructure?"
        desc="Partner with Emicron to build a future-proof technology foundation for your business."
        buttonText="Contact Us"
        to="/contact"
      />
    </div>
  );
}
